import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, FileText, Settings, ArrowLeft, Eye, Trash2, CheckCircle, Archive, Calendar } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { supabase, formatSupabaseError } from "../lib/supabase";
import { LOGO_URL } from "../lib/data";
import { ContentEditor } from "../admin/ContentEditor";
import { SettingsTab } from "../admin/SettingsTab";
import { BookingsTab } from "../admin/BookingsTab";

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const { content, refresh } = useContent();
  const navigate = useNavigate();
  const [tab, setTab] = useState("submissions");

  if (!ready) return <Loader />;
  if (user === false || !user) return <Navigate to="/admin" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white grain">
      <header className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3 group">
              <img src={LOGO_URL} alt="STRAßENWERT" className="h-10 w-auto drop-shadow-[0_0_14px_rgba(255,0,51,0.35)]" />
            </Link>
            <span className="hidden md:block text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              · Admin Panel
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden md:inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.25em] uppercase text-zinc-400 hover:text-white transition-colors" data-testid="admin-goto-site">
              <ArrowLeft size={14} /> Zur Website
            </Link>
            <button onClick={handleLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/60 hover:shadow-[0_0_20px_rgba(255,0,51,0.25)] transition-all">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="border-t border-white/[0.04]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-2 overflow-x-auto">
            <TabBtn active={tab === "submissions"} onClick={() => setTab("submissions")} icon={Inbox} label="Bewerbungen" testid="tab-submissions" />
            <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} icon={Calendar} label="Studio Buchungen" testid="tab-bookings" />
            <TabBtn active={tab === "content"} onClick={() => setTab("content")} icon={FileText} label="Inhalte" testid="tab-content" />
            <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} icon={Settings} label="Einstellungen" testid="tab-settings" />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {tab === "submissions" && <SubmissionsTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "content" && <ContentEditor content={content} onSaved={refresh} />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </main>
  );
}

const Loader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="red-led" />
  </div>
);

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`relative flex items-center gap-2 px-5 py-4 text-[11px] font-bold tracking-[0.25em] uppercase transition-colors ${active ? "text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
    <Icon size={14} />
    {label}
    {active && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />}
  </button>
);

// ============================= Submissions Tab =============================
const SubmissionsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(formatSupabaseError(error));
    else setItems(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const markStatus = async (id, status) => {
    const { error } = await supabase.from("contact_submissions").update({ status }).eq("id", id);
    if (error) {
      toast.error(formatSupabaseError(error));
      return;
    }
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    toast.success("Status aktualisiert.");
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Bewerbung wirklich löschen?")) return;
    const { error } = await supabase.from("contact_submissions").delete().eq("id", id);
    if (error) {
      toast.error(formatSupabaseError(error));
      return;
    }
    setItems((arr) => arr.filter((it) => it.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success("Gelöscht.");
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = {
    all: items.length,
    new: items.filter((i) => i.status === "new").length,
    read: items.filter((i) => i.status === "read").length,
    archived: items.filter((i) => i.status === "archived").length,
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold mb-4">
            <span className="red-led" /> Bewerbungen
          </div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl">Eingang</h2>
        </div>
        <div className="flex items-center gap-2" data-testid="submissions-filters">
          {[["all", "Alle"], ["new", "Neu"], ["read", "Gelesen"], ["archived", "Archiv"]].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} className={`text-[10px] tracking-[0.25em] uppercase px-3 py-2 border transition-colors ${filter === val ? "border-[#ff0033]/60 text-white bg-[#ff0033]/10" : "border-white/10 text-zinc-500 hover:text-white"}`}>
              {label} · {counts[val]}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Lade…</div>}

      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-white/[0.08] bg-[#080808] py-20 text-center">
          <div className="red-led mx-auto mb-4" />
          <div className="text-sm text-zinc-500">Keine Bewerbungen in dieser Ansicht.</div>
        </div>
      )}

      <div className="grid gap-3">
        {filtered.map((s) => (
          <article key={s.id} data-testid={`submission-${s.id}`} className="bg-[#0A0A0A] border border-white/[0.06] p-6 hover:border-[#ff0033]/30 transition-colors">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <span className={`text-[10px] tracking-[0.3em] uppercase font-bold px-2 py-1 border ${s.status === "new" ? "border-[#ff0033]/50 text-[#ff0033] bg-[#ff0033]/10" : s.status === "read" ? "border-white/20 text-zinc-300" : "border-white/10 text-zinc-600"}`}>{s.status}</span>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">{new Date(s.created_at).toLocaleString("de-DE")}</span>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{s.inquiry_type.replace(/-/g, " ")}</span>
                </div>
                <div className="font-display font-bold text-xl uppercase tracking-tight">
                  {s.name}
                  {s.artist_name && <span className="text-[#ff0033] ml-3">· {s.artist_name}</span>}
                </div>
                <div className="mt-1 text-sm text-zinc-400 flex items-center gap-4 flex-wrap">
                  <a href={`mailto:${s.email}`} className="hover:text-white">{s.email}</a>
                  {s.phone && <a href={`tel:${s.phone}`} className="hover:text-white">{s.phone}</a>}
                </div>
                <p className="mt-4 text-sm text-zinc-300 line-clamp-2 max-w-3xl">{s.message}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setSelected(s)} data-testid={`submission-view-${s.id}`} className="p-2.5 border border-white/10 hover:border-[#ff0033]/50 hover:text-[#ff0033] text-zinc-400 transition-colors" title="Details">
                  <Eye size={14} />
                </button>
                {s.status !== "read" && (
                  <button onClick={() => markStatus(s.id, "read")} className="p-2.5 border border-white/10 hover:border-[#ff0033]/50 text-zinc-400 hover:text-[#ff0033] transition-colors" title="Als gelesen markieren">
                    <CheckCircle size={14} />
                  </button>
                )}
                {s.status !== "archived" && (
                  <button onClick={() => markStatus(s.id, "archived")} className="p-2.5 border border-white/10 hover:border-[#ff0033]/50 text-zinc-400 hover:text-[#ff0033] transition-colors" title="Archivieren">
                    <Archive size={14} />
                  </button>
                )}
                <button onClick={() => deleteItem(s.id)} data-testid={`submission-delete-${s.id}`} className="p-2.5 border border-white/10 hover:border-red-500/50 hover:text-red-500 text-zinc-400 transition-colors" title="Löschen">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl bg-[#0A0A0A] border border-white/[0.08] p-8 md:p-10 my-10" data-testid="submission-detail">
            <div className="red-led absolute top-5 right-5" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">{selected.inquiry_type.replace(/-/g, " ")}</div>
            <h3 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mt-3">
              {selected.name}
              {selected.artist_name && <span className="text-zinc-500 block text-xl mt-1">/ {selected.artist_name}</span>}
            </h3>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <Info label="E-Mail" value={<a href={`mailto:${selected.email}`} className="hover:text-[#ff0033]">{selected.email}</a>} />
              <Info label="Telefon" value={selected.phone ? <a href={`tel:${selected.phone}`} className="hover:text-[#ff0033]">{selected.phone}</a> : "—"} />
              <Info label="Status" value={selected.status} />
              <Info label="Datum" value={new Date(selected.created_at).toLocaleString("de-DE")} />
            </div>
            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Nachricht</div>
              <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setSelected(null)} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-white/30">Schließen</button>
              <a href={`mailto:${selected.email}`} className="text-[10px] tracking-[0.3em] uppercase font-bold bg-white text-black px-5 py-2.5 hover:shadow-[0_0_20px_rgba(255,0,51,0.3)] transition-shadow">Per E-Mail antworten</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-1">{label}</div>
    <div className="text-zinc-200">{value}</div>
  </div>
);
